import {
  nonKeyframeableVideoMessage,
  type QuestionnaireChatMessage,
  type QuestionnaireOperationEffect,
  type QuestionnaireReceiptAsset,
} from "@digital-twins/shared-types";
import type { TFunction } from "i18next";

/**
 * What a message's material did, in the builder's words: the one renderer
 * for a collect's skips (its `collect_assets` effects, from a Save or a
 * receipt turn's stored message), a paste's committed receipt
 * (`materialReceipt`, C12-1), and the language line of a chat revision's or
 * re-plan's reply (`notInStudyLanguage`; its summary says what it added). It
 * reads stored typed fields only, so a reload shows the same lines, in the
 * order added, updated, removed, kept. Every asset is named as the receipt
 * stored it when its commit was written: a receipt states what happened then,
 * and a later swap or rename never rewrites it. Each list is a line and one
 * bullet per name, so a name that is a sentence keeps its own punctuation.
 */
export function userMessageReceiptLines(
  message: {
    effects?: ReadonlyArray<QuestionnaireOperationEffect>;
    materialReceipt?: QuestionnaireChatMessage["materialReceipt"];
    notInStudyLanguage?: QuestionnaireChatMessage["notInStudyLanguage"];
  },
  t: TFunction<"app">,
): Array<string> {
  const lines = collectedSkipLines(message.effects ?? [], t);
  const receipt = message.materialReceipt;
  const listed = (heading: string, assets: ReadonlyArray<QuestionnaireReceiptAsset>) =>
    [heading, ...assets.map(({ name }) => `- ${name}`)].join("\n");
  const notInLanguage = (
    receipt?.notInStudyLanguage ??
    message.notInStudyLanguage ??
    []
  ).map(({ name }) => name);
  const languageLine =
    notInLanguage.length > 0
      ? [
          t("questionnaire.materialReceipt.notInStudyLanguage", {
            names: notInLanguage,
          }),
        ]
      : [];
  if (!receipt) return [...lines, ...languageLine];
  if (receipt.added.length > 0) {
    lines.push(
      listed(
        t("questionnaire.materialReceipt.added", {
          count: receipt.added.length,
        }),
        receipt.added,
      ),
    );
  }
  if (receipt.updated?.length) {
    lines.push(
      listed(t("questionnaire.materialReceipt.updated"), receipt.updated),
    );
  }
  if (receipt.removed.length > 0) {
    lines.push(
      listed(t("questionnaire.materialReceipt.removed"), receipt.removed),
    );
  }
  if (receipt.kept?.length) {
    lines.push(listed(t("questionnaire.materialReceipt.kept"), receipt.kept));
  }
  return [...lines, ...languageLine];
}

/**
 * What a collect skipped: each link refused (a video host with no
 * keyframes; its reason is still English, `nonKeyframeableVideoMessage`) and
 * the links already attached.
 */
function collectedSkipLines(
  effects: ReadonlyArray<QuestionnaireOperationEffect>,
  t: TFunction<"app">,
): Array<string> {
  const linksOf = (kind: QuestionnaireOperationEffect["kind"]) =>
    effects.flatMap((effect) =>
      effect.kind === kind && effect.url ? [effect.url] : [],
    );
  const refused = linksOf("collected_link_refused").map(
    (url) => `${url}: ${nonKeyframeableVideoMessage(url) ?? ""}`,
  );
  // A re-add of a URL that's already attached is a no-op, not a duplicate.
  const attached = linksOf("collected_link_already_attached");
  return attached.length > 0
    ? [
        ...refused,
        t("questionnaire.materialReceipt.alreadyAttached", {
          count: attached.length,
          links: attached.join(", "),
        }),
      ]
    : refused;
}
